import { Transition } from '@headlessui/react';
import cn from 'classnames';
import { cloneDeep, merge, values } from 'lodash';
import { FC, Fragment, PropsWithChildren, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FdWarningDanger } from '@qodly/icons';
import ControlledSwitch from '../ControlledSwitch';
import { IModal, ModalCloseReason, ModalType } from '~shared/types/modal';
import Button from './Button';
import Icon from './Icon';

interface ICloseModalParams {
  id: string;
  reason?: ModalCloseReason;
}

interface IEditModalParams {
  id: string;
  views?: IModal['views'];
  title?: IModal['title'];
}

interface IModalProps extends IModal {
  width?: number;
  hasOverlay?: boolean;
  onClose: (params: ICloseModalParams) => void;
  onEdit: (params: IEditModalParams) => void;
}

export const Modal: FC<IModalProps> = ({
  id,
  title,
  message,
  icon,
  type,
  color,
  zIndex = 3000,
  views,
  width = 520,
  hasOverlay = true,
  onClose,
  onEdit,
}) => {
  const handleClose = (reason?: ModalCloseReason) => {
    onClose({
      id,
      reason,
    });
  };

  useEffect(() => {
    function handleKeyUp(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        handleClose(ModalCloseReason.CANCELED);
      }
    }

    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const tips = merge(
    values(views?.toggles).flatMap((toggle) => {
      return toggle.value ? toggle.warnings || [] : [];
    }),
    views?.tips,
  );

  const toggleOption = (optionKey: string, checked: boolean) => {
    if (!views?.toggles) return;
    const update = cloneDeep(views);
    if (update.toggles?.[optionKey]) update.toggles[optionKey].value = checked;

    onEdit({
      id,
      views: update,
    });
  };

  return ReactDOM.createPortal(
    <Transition appear show={true} as={Fragment}>
      <div
        className={cn(
          'absolute inset-0 flex h-full w-full items-center justify-center overflow-y-auto',
          { 'bg-black bg-opacity-80': hasOverlay },
        )}
        style={{ zIndex }}
        onClick={() => handleClose(ModalCloseReason.CANCELED)}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div
            data-cy="confirmation-modal-cy"
            className="relative rounded-md border border-grey-50 bg-grey-900"
            style={{ width }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center gap-3 p-6 px-12">
              {icon ? (
                <div className="flex items-center justify-center">
                  <Icon name={icon} color={color} className='w-6 h-6' />
                </div>
              ) : null}
              <If condition={title}>
                <h4 className="w-full text-wrap text-lg font-bold leading-6 text-white text-center">
                  {title}
                </h4>
              </If>
              <p className="w-full text-wrap text-center text-sm font-medium text-white">
                {message}
              </p>

              <div className="text-xs text-white">
                {values(views?.toggles).map((toggle) => {
                  return (
                    <div key={toggle.key}>
                      <label htmlFor={toggle.key} className="flex items-center">
                        <ControlledSwitch
                          size="xs"
                          value={toggle.value}
                          onChange={(checked) =>
                            toggleOption(toggle.key, checked)
                          }
                        />
                        <span className="ml-1">{toggle.title}</span>
                      </label>
                    </div>
                  );
                })}
              </div>

              <WarningsList warnings={tips} />

              <div className="flex items-center gap-4">
                {type === ModalType.CONFIRM ? (
                  <>
                    <Button
                      autoFocus
                      color={color}
                      onClick={() => handleClose(ModalCloseReason.CONFIRMED)}
                    >
                      Confirm
                    </Button>
                    <Button
                      color={color}
                      variant="outlined"
                      onClick={() => handleClose(ModalCloseReason.CANCELED)}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button autoFocus onClick={() => handleClose()}>
                    OK
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Transition.Child>
      </div>
    </Transition>,
    document.querySelector('body')!,
  );
};

const If = (props: PropsWithChildren<{ condition: any | (() => boolean) }>) =>
  props.condition ? <>{props.children}</> : null;

const WarningsList: FC<{
  warnings: NonNullable<NonNullable<IModal['views']>['tips']>;
}> = (props) => {
  if (!props.warnings) return null;

  return (
    <Transition appear show={!!props.warnings.length} as={Fragment}>
      <div className="flex flex-col gap-1 rounded-sm bg-grey-600 p-2 text-xs text-white">
        {props.warnings.map((warning, index) => (
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
            key={`modal-warnings-${index}`}
          >
            <div className="flex flex-1 items-center gap-1 rounded-sm px-2 py-1">
              <FdWarningDanger
                className={cn(
                  {
                    'h-3 w-3': true,
                    'text-yellow-500': warning.type === 'warning',
                    'text-red-400': warning.type === 'error',
                    'text-primary-dark': warning.type === 'info',
                  },
                  'mr-3',
                )}
              />
              <div className="flex-1">{warning.message}</div>
            </div>
          </Transition.Child>
        ))}
      </div>
    </Transition>
  );
};
